import React, { useState, useEffect } from 'react';
import { 
    Sparkles, 
    X, 
    Calendar, 
    Search, 
    Shield, 
    Satellite, 
    Camera, 
    Share2, 
    Compass, 
    Wind, 
    Users, 
    FolderOpen, 
    Bot, 
    CheckCircle2, 
    Layers,
    ArrowUpRight,
    Flame,
    GitCompare,
    ShoppingBag,
    MapPin,
    LifeBuoy,
    Zap,
    Award
} from 'lucide-react';
import { User } from '../types';

export interface PlatformFeature {
    id: string;
    title: string;
    description: string;
    category: string;
    badge: string;
    icon: string;
    action_tab?: string;
    released_at: string;
    created_at?: string;
}

interface FeatureListModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User | null;
    onNavigateTab?: (tab: string) => void;
}

export const FeatureListModal: React.FC<FeatureListModalProps> = ({
    isOpen,
    onClose,
    user,
    onNavigateTab
}) => {
    const [features, setFeatures] = useState<PlatformFeature[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('Alle');

    const fetchFeatures = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/features');
            const data = await res.json();
            if (data.features) {
                setFeatures(data.features);
            }
        } catch (e) {
            console.error('Fehler beim Laden der Funktionsliste:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchFeatures();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const renderIcon = (iconName: string) => {
        const props = { className: "w-5 h-5" };
        switch (iconName) {
            case 'Satellite': return <Satellite {...props} className="w-5 h-5 text-emerald-400" />;
            case 'Compass': return <Compass {...props} className="w-5 h-5 text-orange-400" />;
            case 'FolderOpen': return <FolderOpen {...props} className="w-5 h-5 text-amber-400" />;
            case 'GitCompare': return <GitCompare {...props} className="w-5 h-5 text-purple-400" />;
            case 'Share2': return <Share2 {...props} className="w-5 h-5 text-sky-400" />;
            case 'Users': return <Users {...props} className="w-5 h-5 text-pink-400" />;
            case 'ShoppingBag': return <ShoppingBag {...props} className="w-5 h-5 text-emerald-400" />;
            case 'MapPin': return <MapPin {...props} className="w-5 h-5 text-orange-400" />;
            case 'Bot': return <Bot {...props} className="w-5 h-5 text-indigo-400" />;
            case 'LifeBuoy': return <LifeBuoy {...props} className="w-5 h-5 text-sky-400" />;
            case 'Shield': return <Shield {...props} className="w-5 h-5 text-emerald-400" />;
            case 'Wind': return <Wind {...props} className="w-5 h-5 text-cyan-400" />;
            case 'Camera': return <Camera {...props} className="w-5 h-5 text-purple-400" />;
            case 'Zap': return <Zap {...props} className="w-5 h-5 text-yellow-400" />;
            case 'Award': return <Award {...props} className="w-5 h-5 text-amber-400" />;
            default: return <Sparkles {...props} className="w-5 h-5 text-sky-400" />;
        }
    };

    const formatDate = (dateStr: string) => {
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('de-CH', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const categories = ['Alle', ...Array.from(new Set(features.map(f => f.category).filter(Boolean)))];

    const filteredFeatures = features.filter(f => {
        const matchesCategory = selectedCategory === 'Alle' || f.category === selectedCategory;
        const matchesSearch = 
            f.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            f.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (f.category && f.category.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden relative">
                
                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-white/5 bg-slate-950/70 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-sky-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-sky-500/20 text-white shrink-0">
                            <Sparkles className="w-5 h-5 animate-pulse" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-lg sm:text-2xl font-black text-white uppercase tracking-tight">
                                    Funktionsliste & Neuerungen
                                </h2>
                                <span className="hidden sm:flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 items-center gap-1">
                                    <Flame className="w-3 h-3 text-emerald-400" /> Neueste zuerst
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">
                                Übersicht aller neuen und erweiterten Funktionen der Plattform
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors shrink-0 mr-1 sm:mr-2"
                        title="Schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Filter & Search Bar */}
                <div className="p-3 sm:p-4 bg-slate-950/40 border-b border-white/5 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between shrink-0">
                    {/* Category Tabs (mit horizontalem Scrollen) */}
                    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1 shrink-0">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                    selectedCategory === cat 
                                        ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30 shadow-md shadow-sky-950/30' 
                                        : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent'
                                }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    {/* Search Input */}
                    <div className="relative min-w-[220px]">
                        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Funktion suchen..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-900/90 border border-white/10 rounded-xl pl-9 pr-4 py-1.5 text-xs text-white placeholder-slate-500 focus:border-sky-500 outline-none"
                        />
                    </div>
                </div>

                {/* Timeline Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-500 gap-3">
                            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-xs font-bold uppercase tracking-wider">Lade Funktionsliste...</p>
                        </div>
                    ) : filteredFeatures.length === 0 ? (
                        <div className="text-center py-16 text-slate-500">
                            <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-bold text-slate-400">Keine Funktionen gefunden</p>
                            <p className="text-xs text-slate-600 mt-1">Passe deine Filterkriterien oder den Suchbegriff an.</p>
                        </div>
                    ) : (
                        <div className="space-y-4 relative before:absolute before:inset-0 before:left-5 before:w-0.5 before:bg-gradient-to-b before:from-sky-500/40 before:via-indigo-500/20 before:to-transparent">
                            {filteredFeatures.map((f, idx) => {
                                const isNew = idx === 0 || f.badge === 'Neu';
                                return (
                                    <div
                                        key={f.id || idx}
                                        className="relative pl-12 group transition-all"
                                    >
                                        {/* Timeline Dot & Icon */}
                                        <div className={`absolute left-2.5 top-3 -translate-x-1/2 w-8 h-8 rounded-xl flex items-center justify-center border transition-all ${isNew ? 'bg-slate-900 border-sky-500 shadow-[0_0_12px_rgba(56,189,248,0.4)]' : 'bg-slate-900 border-white/10 group-hover:border-sky-500/50'}`}>
                                            {renderIcon(f.icon)}
                                        </div>

                                        {/* Card Box */}
                                        <div className={`p-4 sm:p-5 rounded-2xl border transition-all ${isNew ? 'bg-gradient-to-br from-slate-900 via-slate-900/90 to-sky-950/30 border-sky-500/30 shadow-xl shadow-sky-950/20' : 'bg-slate-900/70 border-white/5 hover:border-white/15'}`}>
                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider ${f.badge === 'Neu' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : f.badge === 'Highlight' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : f.badge === 'Pro' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-sky-500/20 text-sky-400 border border-sky-500/30'}`}>
                                                        {f.badge || 'Neu'}
                                                    </span>

                                                    <span className="text-[11px] font-bold text-slate-400">
                                                        {f.category}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {formatDate(f.released_at)}
                                                    </span>
                                                </div>
                                            </div>

                                            <h3 className="text-base sm:text-lg font-black text-white mb-2 group-hover:text-sky-300 transition-colors">
                                                {f.title}
                                            </h3>

                                            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mb-4">
                                                {f.description}
                                            </p>

                                            {f.action_tab && onNavigateTab && (
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() => {
                                                            onClose();
                                                            onNavigateTab(f.action_tab!);
                                                        }}
                                                        className="px-3.5 py-1.5 rounded-xl bg-sky-600/20 hover:bg-sky-600 text-sky-400 hover:text-white border border-sky-500/30 transition-all text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-md shadow-sky-950/20"
                                                    >
                                                        Jetzt ausprobieren <ArrowUpRight className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/5 bg-slate-950/80 flex items-center justify-between text-xs text-slate-500 shrink-0">
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Regelmäßige Updates & kontinuierliche Plattform-Evolution</span>
                    </div>
                    <span className="font-bold text-slate-400">
                        {filteredFeatures.length} {filteredFeatures.length === 1 ? 'Eintrag' : 'Einträge'}
                    </span>
                </div>
            </div>
        </div>
    );
};
